export const API_BASE = window.desktop?.apiBaseUrl ?? "/api";
const TOKEN_KEY = "email-manager-session";
let refreshPromise: Promise<boolean> | null = null;

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

export const session = {
  get token() { return sessionStorage.getItem(TOKEN_KEY); },
  set token(value: string | null) {
    if (value) sessionStorage.setItem(TOKEN_KEY, value);
    else sessionStorage.removeItem(TOKEN_KEY);
  }
};

async function tryRefresh() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include"
    }).then(async (response) => {
      if (!response.ok) return false;
      const result = await response.json();
      session.token = result.token;
      return true;
    }).catch(() => false).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  includeAuth = true,
  retry = true
): Promise<T> {
  const headers = new Headers(options.headers);
  if (includeAuth && session.token) headers.set("Authorization", `Bearer ${session.token}`);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: "include"
  });
  if (response.status === 401 && includeAuth && retry && await tryRefresh())
    return request<T>(path, options, includeAuth, false);
  if (response.status === 401 && includeAuth) {
    session.token = null;
    window.dispatchEvent(new Event("email-manager-unauthorized"));
  }
  if (!response.ok) {
    let message = "Something went wrong. Please try again.";
    try {
      const data = await response.json();
      message = data.message ?? data.title ?? message;
    } catch { /* keep fallback */ }
    throw new ApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function requestBlob(path: string, retry = true): Promise<Blob> {
  const headers = new Headers();
  if (session.token) headers.set("Authorization", `Bearer ${session.token}`);
  const response = await fetch(`${API_BASE}${path}`, { headers, credentials: "include" });
  if (response.status === 401 && retry && await tryRefresh()) return requestBlob(path, false);
  if (response.status === 401) {
    session.token = null;
    window.dispatchEvent(new Event("email-manager-unauthorized"));
  }
  if (!response.ok) {
    let message = "Something went wrong. Please try again.";
    try {
      const data = await response.json();
      message = data.message ?? data.title ?? message;
    } catch { /* keep fallback */ }
    throw new ApiError(message, response.status);
  }
  return response.blob();
}

export const api = {
  login: (username: string, password: string) =>
    request<{ token: string; user: import("../types").User }>(
      "/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }, false),
  logout: () => request<void>("/auth/logout", { method: "POST" }, false),
  me: () => request<import("../types").User>("/auth/me"),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>("/auth/change-password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) }),
  dashboard: () => request<import("../types").Dashboard>("/dashboard"),
  accounts: () => request<import("../types").EmailAccount[]>("/accounts"),
  createAccount: (data: unknown) => request<import("../types").EmailAccount>("/accounts", { method: "POST", body: JSON.stringify(data) }),
  updateAccount: (id: string, data: unknown) => request<import("../types").EmailAccount>(`/accounts/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  createAccountsBulk: (data: unknown) => request<import("../types").BulkAccountResult>("/accounts/bulk", { method: "POST", body: JSON.stringify(data) }),
  customProviders: () => request<import("../types").CustomEmailProvider[]>("/account-providers"),
  createCustomProvider: (data: unknown) => request<import("../types").CustomEmailProvider>("/account-providers", { method: "POST", body: JSON.stringify(data) }),
  updateCustomProvider: (id: string, data: unknown) => request<import("../types").CustomEmailProvider>(`/account-providers/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteCustomProvider: (id: string) => request<void>(`/account-providers/${id}`, { method: "DELETE" }),
  disableAccount: (id: string) => request<void>(`/accounts/${id}`, { method: "DELETE" }),
  testAccount: (id: string) => request<{ success: boolean; message: string }>(`/accounts/${id}/test`, { method: "POST" }),
  syncAccount: (id: string) => request<{ imported: number; message: string }>(`/accounts/${id}/sync`, { method: "POST" }),
  startOAuth: (provider: string) => request<{ authorizationUrl: string }>(`/accounts/oauth/${provider}/start`, { method: "POST" }),
  messages: (params: Record<string, string | number | boolean | undefined>) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => { if (value !== undefined && value !== "") query.set(key, String(value)); });
    return request<import("../types").PagedMessages>(`/messages?${query}`);
  },
  mailFolders: () => request<import("../types").MailFolder[]>("/messages/folders"),
  bulkMessages: (messageIds: string[], action: string) =>
    request<{ updated: number }>("/messages/bulk", {
      method: "POST",
      body: JSON.stringify({ messageIds, action })
    }),
  message: (id: string) => request<import("../types").EmailDetail>(`/messages/${id}`),
  updateMessage: (id: string, data: unknown) => request<void>(`/messages/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteMessage: (id: string) => request<void>(`/messages/${id}`, { method: "DELETE" }),
  sendMessage: (form: FormData) => request<{ id: string; message: string }>("/messages/send", { method: "POST", body: form }),
  createDraft: (data: unknown) => request<import("../types").EmailSummary>("/messages/drafts", { method: "POST", body: JSON.stringify(data) }),
  updateDraft: (id: string, data: unknown) => request<import("../types").EmailSummary>(`/messages/drafts/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  templates: () => request<import("../types").EmailTemplate[]>("/templates"),
  createTemplate: (data: unknown) => request<import("../types").EmailTemplate>("/templates", { method: "POST", body: JSON.stringify(data) }),
  updateTemplate: (id: string, data: unknown) => request<import("../types").EmailTemplate>(`/templates/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteTemplate: (id: string) => request<void>(`/templates/${id}`, { method: "DELETE" }),
  uploadTemplateAttachment: (id: string, file: File) => {
    const form = new FormData(); form.append("file", file);
    return request<import("../types").EmailTemplate>(`/templates/${id}/attachments`, { method: "POST", body: form });
  },
  removeTemplateAttachment: (templateId: string, attachmentId: string) =>
    request<void>(`/templates/${templateId}/attachments/${attachmentId}`, { method: "DELETE" }),
  contacts: (search = "") =>
    request<import("../types").EmailContact[]>(`/contacts?search=${encodeURIComponent(search)}&take=200`),
  createContact: (data: unknown) =>
    request<import("../types").EmailContact>("/contacts", { method: "POST", body: JSON.stringify(data) }),
  updateContact: (id: string, data: unknown) =>
    request<import("../types").EmailContact>(`/contacts/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  deleteContact: (id: string) => request<void>(`/contacts/${id}`, { method: "DELETE" }),
  users: () => request<import("../types").User[]>("/users"),
  createUser: (data: unknown) => request<import("../types").User>("/users", { method: "POST", body: JSON.stringify(data) }),
  updateUser: (id: string, data: unknown) => request<import("../types").User>(`/users/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  resetPassword: (id: string) => request<void>(`/users/${id}/reset-password`, { method: "POST" }),
  uploadUserPhoto: (id: string, file: File) => {
    const form = new FormData(); form.append("photo", file);
    return request<void>(`/users/${id}/photo`, { method: "POST", body: form });
  },
  userPhoto: (id: string) => requestBlob(`/users/${id}/photo`),
  userAccountAccess: (id: string) => request<import("../types").AccountAccessAssignment[]>(`/users/${id}/accounts`),
  setUserAccountAccess: (userId: string, accountId: string, data: unknown) => request<void>(`/users/${userId}/accounts/${accountId}`, { method: "PUT", body: JSON.stringify(data) }),
  removeUserAccountAccess: (userId: string, accountId: string) => request<void>(`/users/${userId}/accounts/${accountId}`, { method: "DELETE" }),
  roles: () => request<import("../types").Role[]>("/roles"),
  createRole: (data: unknown) => request<import("../types").Role>("/roles", { method: "POST", body: JSON.stringify(data) }),
  updateRole: (id: string, data: unknown) => request<import("../types").Role>(`/roles/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  roleAccountAccess: (id: string) => request<import("../types").AccountAccessAssignment[]>(`/roles/${id}/accounts`),
  setRoleAccountAccess: (roleId: string, accountId: string, data: unknown) => request<void>(`/roles/${roleId}/accounts/${accountId}`, { method: "PUT", body: JSON.stringify(data) }),
  removeRoleAccountAccess: (roleId: string, accountId: string) => request<void>(`/roles/${roleId}/accounts/${accountId}`, { method: "DELETE" }),
  audit: () => request<import("../types").AuditLog[]>("/audit"),
  verifyAudit: () => request<{ valid: boolean; checkedEntries: number }>("/audit/verify"),
  archiveSettings: () => request<import("../types").ArchiveSettings>("/settings/archive"),
  updateArchiveSettings: (data: unknown) => request<void>("/settings/archive", { method: "PUT", body: JSON.stringify(data) }),
  systemHealth: () => request<{
    api: string; database: string; connectedAccounts: number; attentionAccounts: number;
    pendingDeliveries?: number; failedDeliveries?: number; lastMailboxSync?: string; lastCheckedAt: string;
  }>("/settings/health"),
  attachmentUrl: (messageId: string, attachmentId: string) => `${API_BASE}/messages/${messageId}/attachments/${attachmentId}`
};


